import { useTranslation } from 'react-i18next';
import { formatDateForDisplay } from '../../../utils';

type ContractConcludedInfoProps = {
    /** ISO timestamp of the contract conclusion, or null for a prepay booking. */
    contractConcludedAt?: string | null;
    paymentStatus?: string;
    /** Set when an organization covers the booking (#1926): then nothing is
     *  claimed from the customer, and the open amount must not read as a claim. */
    coveringOrganizationName?: string | null;
    /** #3219: the booking location's zone; the station default when omitted. */
    timeZone?: string;
};

/**
 * The CONTRACT axis on a booking's detail page (#1703) — beside the money axis
 * and never folded into it.
 *
 * A postpay booking is `booked` and `unpaid` at the same time on purpose: the
 * contract is concluded and the customer settles by invoice after use. The
 * payment badge above stays the money question; this says why an open amount is
 * expected, so it reads as the claim it is rather than as an oversight.
 *
 * Renders nothing without a concluded contract, so every prepay booking looks
 * exactly as it did before.
 *
 * Shared by the apartment and venue detail pages — postpay v1 covers both use
 * cases (D9), and the two pages are near-copies of each other, so an inline
 * block would be duplicated wording that drifts on the next edit.
 */
const ContractConcludedInfo = ({
    contractConcludedAt,
    paymentStatus,
    coveringOrganizationName,
    timeZone,
}: ContractConcludedInfoProps) => {
    const { t } = useTranslation();

    if (!contractConcludedAt) return null;

    return (
        <>
            <p className={'text-sm text-gray-500 mt-1'}>
                {t('booking_details.contract_concluded_at')}:{' '}
                <span className={'font-bold'}>
                    {formatDateForDisplay(contractConcludedAt, timeZone)}
                </span>
            </p>
            {coveringOrganizationName ? (
                <p className={'text-sm text-gray-500'}>
                    {t('booking_details.covered_by_organization', { organization: coveringOrganizationName })}
                </p>
            ) : paymentStatus === 'unpaid' && (
                <p className={'text-sm text-gray-500'}>
                    {t('booking_details.postpay_open_claim')}
                </p>
            )}
        </>
    );
};

export default ContractConcludedInfo;
